import { useQuery } from '@tanstack/react-query'
import { Button, Card, Descriptions, Flex, Space, Tag, Typography } from 'antd'
import {
  Building2,
  Layers,
  Pencil,
  Plus,
  UserPlus,
  Users as UsersIcon
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { organizationsAccessListOptions } from 'src/api/main'
import {
  DateTimeCell,
  MonoValue,
  ResizableDrawer,
  SectionLabel,
  UserCell
} from 'src/components'
import { paths } from 'src/router'
import { composeDisplayName } from 'src/utils/string'

import type { OrgUnit } from '../types'
import { KIND_COLOR, apiTypeToKind, childKindOf } from '../utils'

import { OrgTeamMembersCard } from './OrgTeamMembersCard'

const { Paragraph, Text, Title } = Typography

type Props = {
  workspaceId: number
  unit: OrgUnit | null
  units: OrgUnit[]
  onClose: () => void
  /** Omitted when the viewer may not change the structure. */
  onEdit?: (unit: OrgUnit) => void
  onAddChild?: (unit: OrgUnit) => void
  /** Rendered inside the drawer so antd's nested-drawer push behavior applies. */
  nestedDrawer?: ReactNode
}

const TypeIcon = ({ kind }: { kind: 'division' | 'department' | 'team' }) => {
  if (kind === 'division') return <Building2 size={15} />
  if (kind === 'department') return <Layers size={15} />
  return <UsersIcon size={15} />
}

export const OrgDetailDrawer = ({
  workspaceId,
  unit,
  units,
  onClose,
  onEdit,
  onAddChild,
  nestedDrawer
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const goToUser = useCallback(
    (userId: number) => {
      navigate(
        paths.workspaces.membersPerson(String(workspaceId), String(userId))
      )
      onClose()
    },
    [navigate, onClose, workspaceId]
  )

  const children = useMemo(
    () => (unit ? units.filter((u) => u.parent === unit.id) : []),
    [unit, units]
  )

  const parent = useMemo(
    () =>
      unit?.parent != null
        ? units.find((u) => u.id === unit.parent)
        : undefined,
    [unit, units]
  )

  const { data: membersPage } = useQuery({
    ...organizationsAccessListOptions({
      query: { organization: workspaceId, per_page: 500 }
    }),
    enabled: !!unit && Number.isFinite(workspaceId)
  })

  const members = useMemo(() => {
    if (!unit || !membersPage?.results) return []
    return membersPage.results.filter((m) =>
      m.teams.some((tm) => tm.id === unit.id)
    )
  }, [unit, membersPage])

  if (!unit) return null

  const kind = apiTypeToKind(unit.type)
  const color = KIND_COLOR[kind]
  const head = unit.head_user_detail
  const childKind = childKindOf(kind)

  return (
    <ResizableDrawer
      open={!!unit}
      onClose={onClose}
      defaultWidth={560}
      storageKey="org-detail-drawer-width"
      title={
        <Flex align="center" gap={10}>
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 7,
              background: `${color}1F`,
              color,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <TypeIcon kind={kind} />
          </span>
          <Flex vertical style={{ lineHeight: 1.2, minWidth: 0 }}>
            <Flex align="center" gap={6}>
              <Title level={4} style={{ margin: 0, fontSize: 16 }} ellipsis>
                {unit.name}
              </Title>
              <Tag
                style={{
                  marginInlineEnd: 0,
                  fontSize: 11,
                  background: `${color}1F`,
                  color,
                  border: 'none'
                }}
              >
                {t(`organization.types.${kind}`)}
              </Tag>
            </Flex>
            {parent ? (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {t('organization.detail.inParent', { parent: parent.name })}
              </Text>
            ) : null}
          </Flex>
        </Flex>
      }
      extra={
        <Space size="small">
          {childKind && onAddChild ? (
            <Button
              size="small"
              icon={<Plus size={14} />}
              onClick={() => onAddChild(unit)}
            >
              {t(`organization.actions.add.${childKind}`)}
            </Button>
          ) : null}
          {onEdit ? (
            <Button
              size="small"
              type="primary"
              icon={<Pencil size={14} />}
              onClick={() => onEdit(unit)}
            >
              {t('organization.actions.edit')}
            </Button>
          ) : null}
        </Space>
      }
    >
      <Flex vertical gap={14}>
        {unit.description ? (
          <Paragraph style={{ fontSize: 13, lineHeight: 1.6, margin: 0 }}>
            {unit.description}
          </Paragraph>
        ) : null}

        <Card size="small" styles={{ body: { padding: 0 } }}>
          <Flex>
            <Flex vertical gap={2} style={{ flex: 1, padding: 12 }}>
              <SectionLabel>
                {t('organization.detail.stats.headcount')}
              </SectionLabel>
              <Title
                level={4}
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums'
                }}
              >
                {unit.users_count}
              </Title>
            </Flex>
            <span
              style={{
                width: 1,
                background: 'var(--ant-color-border-secondary)'
              }}
            />
            <Flex vertical gap={2} style={{ flex: 1, padding: 12 }}>
              <SectionLabel>
                {t('organization.detail.stats.subUnits')}
              </SectionLabel>
              <Title
                level={4}
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums'
                }}
              >
                {children.length}
              </Title>
            </Flex>
          </Flex>
        </Card>

        <Card size="small" title={t('organization.detail.lead.title')}>
          {head ? (
            <Flex align="center" justify="space-between" gap={10}>
              <UserCell
                firstName={head.first_name}
                lastName={head.last_name}
                email={head.email}
                color={color}
                size="lg"
                onClick={() => goToUser(head.id)}
              />
              {onEdit ? (
                <Button size="small" onClick={() => onEdit(unit)}>
                  {t('organization.detail.lead.change')}
                </Button>
              ) : null}
            </Flex>
          ) : (
            <Flex align="center" justify="space-between" gap={10}>
              <Flex align="center" gap={8}>
                <UserPlus size={14} color="var(--ant-color-warning)" />
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {t('organization.detail.lead.none')}
                </Text>
              </Flex>
              {onEdit ? (
                <Button
                  size="small"
                  type="primary"
                  onClick={() => onEdit(unit)}
                >
                  {t('organization.detail.lead.assign')}
                </Button>
              ) : null}
            </Flex>
          )}
        </Card>

        {children.length > 0 ? (
          <Card
            size="small"
            title={
              <Flex align="center" justify="space-between" gap={6}>
                <Flex align="center" gap={6}>
                  <Text strong style={{ fontSize: 13 }}>
                    {t(
                      `organization.detail.subUnits.${apiTypeToKind(children[0].type)}Plural`
                    )}
                  </Text>
                  <Tag style={{ marginInlineEnd: 0, fontSize: 10 }}>
                    {children.length}
                  </Tag>
                </Flex>
                {childKind && onAddChild ? (
                  <Button
                    size="small"
                    type="text"
                    icon={<Plus size={14} />}
                    onClick={() => onAddChild(unit)}
                  >
                    {t('organization.actions.addShort')}
                  </Button>
                ) : null}
              </Flex>
            }
          >
            <Flex vertical gap={6}>
              {children.map((c) => {
                const cKind = apiTypeToKind(c.type)
                const cColor = KIND_COLOR[cKind]
                const cHead = c.head_user_detail

                return (
                  <Flex
                    key={c.id}
                    align="center"
                    justify="space-between"
                    gap={10}
                    style={{
                      padding: '8px 10px',
                      background: 'var(--ant-color-fill-quaternary)',
                      border: '1px solid var(--ant-color-border-secondary)',
                      borderRadius: 6
                    }}
                  >
                    <Flex align="center" gap={8} style={{ minWidth: 0 }}>
                      <span
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: 5,
                          background: `${cColor}1F`,
                          color: cColor,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 11
                        }}
                      >
                        <TypeIcon kind={cKind} />
                      </span>
                      <Flex vertical style={{ lineHeight: 1.25, minWidth: 0 }}>
                        <Text
                          strong
                          style={{ fontSize: 12.5 }}
                          ellipsis={{ tooltip: c.name }}
                        >
                          {c.name}
                        </Text>
                        {cHead ? (
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {composeDisplayName(
                              cHead.first_name,
                              cHead.last_name,
                              cHead.email
                            )}
                          </Text>
                        ) : null}
                      </Flex>
                    </Flex>
                    <Text
                      strong
                      style={{
                        fontSize: 12,
                        fontVariantNumeric: 'tabular-nums',
                        color: cColor
                      }}
                    >
                      {c.users_count}
                    </Text>
                  </Flex>
                )
              })}
            </Flex>
          </Card>
        ) : null}

        <OrgTeamMembersCard
          workspaceId={workspaceId}
          unit={unit}
          color={color}
          members={members}
          allUsers={membersPage?.results ?? []}
          canManage={kind === 'team' && onEdit != null}
          onMemberClick={goToUser}
        />

        <Card size="small" title={t('organization.detail.metadata.title')}>
          <Descriptions
            column={1}
            size="small"
            colon={false}
            styles={{ label: { width: 110 } }}
          >
            <Descriptions.Item label={t('organization.detail.metadata.unitId')}>
              <MonoValue>{String(unit.id)}</MonoValue>
            </Descriptions.Item>
            <Descriptions.Item label={t('organization.detail.metadata.type')}>
              {t(`organization.types.${kind}`)}
            </Descriptions.Item>
            <Descriptions.Item label={t('organization.detail.metadata.parent')}>
              {parent ? parent.name : t('organization.browse.workspaceRoot')}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('organization.detail.metadata.created')}
            >
              <DateTimeCell value={unit.date_created} variant="dateTime" />
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </Flex>
      {nestedDrawer}
    </ResizableDrawer>
  )
}
